import AsyncStorage from '@react-native-async-storage/async-storage';
import messaging from '@react-native-firebase/messaging';
import DeviceInfo from 'react-native-device-info';
import {Linking, Platform} from 'react-native';
import i18n from '../../i18n';

const getPermissionsAndroid = () => {
    if (Platform.OS === 'android') {
        return require('react-native').PermissionsAndroid;
    }

    return {
        RESULTS: {
            GRANTED: 'granted',
            DENIED: 'denied',
            NEVER_ASK_AGAIN: 'never_ask_again',
        },
        PERMISSIONS: {
            POST_NOTIFICATIONS: 'android.permission.POST_NOTIFICATIONS',
        },
        check: async () => false,
        request: async () => 'denied',
    };
};

export const PUSH_NOTIFICATION_STORAGE_KEYS = {
    enabled: 'pushNotifications:lastKnownEnabled',
    permissionStatus: 'pushNotifications:permissionStatus',
    tokenRegistered: 'pushNotifications:tokenRegistered',
    lastSyncResult: 'pushNotifications:lastSyncResult',
    lastToken: 'pushNotifications:lastToken',
    pendingOpenMessage: 'pushNotifications:pendingOpenMessage',
    // Set once the one-time first-login "enable notifications" prompt has been
    // shown, so we never nag the user again on subsequent app launches.
    firstLoginPrompted: 'pushNotifications:firstLoginPrompted',
};

export const PUSH_PERMISSION_STATUS = {
    GRANTED: 'granted',
    DENIED: 'denied',
    BLOCKED: 'blocked',
    NOT_DETERMINED: 'not-determined',
    UNAVAILABLE: 'unavailable',
};

const IOS_AUTHORIZED_STATUSES = new Set([1, 2]);

const stringifyRemoteData = (data = {}) => Object.keys(data || {}).reduce((accumulator, key) => {
    const value = data[key];
    accumulator[key] = value === undefined || value === null ? '' : String(value);
    return accumulator;
}, {});

const getLocale = () => {
    try {
        return globalThis.Intl?.DateTimeFormat?.().resolvedOptions().locale || undefined;
    } catch (_error) {
        return undefined;
    }
};

const getTimezone = () => {
    try {
        return globalThis.Intl?.DateTimeFormat?.().resolvedOptions().timeZone || undefined;
    } catch (_error) {
        return undefined;
    }
};

const safeJsonParse = (value, fallbackValue = null) => {
    if (!value) {
        return fallbackValue;
    }

    try {
        return JSON.parse(value);
    } catch (_error) {
        return fallbackValue;
    }
};

const normalizePermissionStatus = (status) => {
    const PermissionsAndroid = getPermissionsAndroid();

    if (!status) {
        return PUSH_PERMISSION_STATUS.NOT_DETERMINED;
    }

    if (status === PermissionsAndroid.RESULTS.GRANTED || status === 1 || status === true) {
        return PUSH_PERMISSION_STATUS.GRANTED;
    }

    if (status === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
        return PUSH_PERMISSION_STATUS.BLOCKED;
    }

    if (status === PermissionsAndroid.RESULTS.DENIED) {
        return PUSH_PERMISSION_STATUS.DENIED;
    }

    return PUSH_PERMISSION_STATUS.NOT_DETERMINED;
};

export const isNativePushSupported = () => Platform.OS === 'android' || Platform.OS === 'ios';

export async function loadStoredPushNotificationState() {
    const values = await AsyncStorage.multiGet(Object.values(PUSH_NOTIFICATION_STORAGE_KEYS));
    const mappedValues = Object.fromEntries(values);
    const enabledValue = mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.enabled];

    return {
        isEnabled: enabledValue == null ? null : enabledValue === 'true',
        permissionStatus: mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.permissionStatus] || PUSH_PERMISSION_STATUS.NOT_DETERMINED,
        tokenRegistered: mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.tokenRegistered] === 'true',
        lastSyncResult: safeJsonParse(mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.lastSyncResult], null),
        lastToken: mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.lastToken] || '',
        pendingOpenMessage: safeJsonParse(mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.pendingOpenMessage], null),
        firstLoginPrompted: mappedValues[PUSH_NOTIFICATION_STORAGE_KEYS.firstLoginPrompted] === 'true',
    };
}

export async function persistPushNotificationState(partialState = {}) {
    const entries = [];

    if (Object.prototype.hasOwnProperty.call(partialState, 'isEnabled')) {
        entries.push([PUSH_NOTIFICATION_STORAGE_KEYS.enabled, partialState.isEnabled ? 'true' : 'false']);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'permissionStatus')) {
        entries.push([PUSH_NOTIFICATION_STORAGE_KEYS.permissionStatus, partialState.permissionStatus || PUSH_PERMISSION_STATUS.NOT_DETERMINED]);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'tokenRegistered')) {
        entries.push([PUSH_NOTIFICATION_STORAGE_KEYS.tokenRegistered, partialState.tokenRegistered ? 'true' : 'false']);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'lastToken')) {
        entries.push([PUSH_NOTIFICATION_STORAGE_KEYS.lastToken, partialState.lastToken || '']);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'lastSyncResult')) {
        entries.push([
            PUSH_NOTIFICATION_STORAGE_KEYS.lastSyncResult,
            JSON.stringify(partialState.lastSyncResult || null),
        ]);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'pendingOpenMessage')) {
        entries.push([
            PUSH_NOTIFICATION_STORAGE_KEYS.pendingOpenMessage,
            JSON.stringify(partialState.pendingOpenMessage || null),
        ]);
    }

    if (Object.prototype.hasOwnProperty.call(partialState, 'firstLoginPrompted')) {
        entries.push([PUSH_NOTIFICATION_STORAGE_KEYS.firstLoginPrompted, partialState.firstLoginPrompted ? 'true' : 'false']);
    }

    if (!entries.length) {
        return;
    }

    await AsyncStorage.multiSet(entries);
}

export async function getNotificationPermissionStatus() {
    if (!isNativePushSupported()) {
        return PUSH_PERMISSION_STATUS.UNAVAILABLE;
    }

    if (Platform.OS === 'ios') {
        const status = await messaging().hasPermission();
        const permissionStatus = IOS_AUTHORIZED_STATUSES.has(status)
            ? PUSH_PERMISSION_STATUS.GRANTED
            : PUSH_PERMISSION_STATUS.NOT_DETERMINED;

        await persistPushNotificationState({ permissionStatus });
        return permissionStatus;
    }

    const PermissionsAndroid = getPermissionsAndroid();

    if (Platform.Version < 33) {
        await persistPushNotificationState({ permissionStatus: PUSH_PERMISSION_STATUS.GRANTED });
        return PUSH_PERMISSION_STATUS.GRANTED;
    }

    const hasPermission = await PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS);
    const permissionStatus = hasPermission ? PUSH_PERMISSION_STATUS.GRANTED : PUSH_PERMISSION_STATUS.DENIED;
    await persistPushNotificationState({ permissionStatus });
    return permissionStatus;
}

export async function requestAndroidNotificationPermission() {
    if (!isNativePushSupported()) {
        return PUSH_PERMISSION_STATUS.UNAVAILABLE;
    }

    if (Platform.OS === 'ios') {
        const status = await messaging().requestPermission({
            alert: true,
            badge: true,
            sound: true,
            provisional: false,
        });
        const permissionStatus = IOS_AUTHORIZED_STATUSES.has(status)
            ? PUSH_PERMISSION_STATUS.GRANTED
            : PUSH_PERMISSION_STATUS.DENIED;

        await persistPushNotificationState({ permissionStatus });
        return permissionStatus;
    }

    const PermissionsAndroid = getPermissionsAndroid();

    if (Platform.Version < 33) {
        await persistPushNotificationState({ permissionStatus: PUSH_PERMISSION_STATUS.GRANTED });
        return PUSH_PERMISSION_STATUS.GRANTED;
    }

    const currentStatus = await getNotificationPermissionStatus();
    if (currentStatus === PUSH_PERMISSION_STATUS.GRANTED) {
        return currentStatus;
    }

    const result = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS, {
        title: i18n.t('push_permission_title'),
        message: i18n.t('push_permission_message'),
        buttonPositive: i18n.t('push_permission_allow'),
        buttonNegative: i18n.t('push_permission_not_now'),
        buttonNeutral: i18n.t('push_permission_ask_later'),
    });

    const permissionStatus = normalizePermissionStatus(result);
    await persistPushNotificationState({ permissionStatus });
    return permissionStatus;
}

export async function openSystemNotificationSettings() {
    try {
        await Linking.openSettings();
        return true;
    } catch (_error) {
        return false;
    }
}

export async function getFcmToken() {
    if (!isNativePushSupported()) {
        return '';
    }

    await messaging().registerDeviceForRemoteMessages();
    const token = await messaging().getToken();
    await persistPushNotificationState({ lastToken: token || '' });
    return token || '';
}

export async function deleteFcmToken() {
    if (!isNativePushSupported()) {
        return;
    }

    try {
        await messaging().deleteToken();
    } finally {
        await persistPushNotificationState({ lastToken: '' });
    }
}

export async function buildDeviceMetadata() {
    const [deviceId, deviceName] = await Promise.all([
        DeviceInfo.getUniqueId(),
        DeviceInfo.getDeviceName().catch((_error) => ''),
    ]);

    return {
        platform: Platform.OS === 'ios' ? 'ios' : 'android',
        deviceId: deviceId || undefined,
        deviceName: deviceName || DeviceInfo.getModel() || undefined,
        appVersion: DeviceInfo.getVersion() || undefined,
        osVersion: `${Platform.OS === 'ios' ? 'iOS' : 'Android'} ${DeviceInfo.getSystemVersion()}`,
        locale: getLocale(),
        timezone: getTimezone(),
    };
}

export function normalizeRemoteMessage(remoteMessage) {
    if (!remoteMessage) {
        return null;
    }

    const notification = remoteMessage.notification || {};
    const data = stringifyRemoteData(remoteMessage.data);

    return {
        messageId: remoteMessage.messageId || remoteMessage?.message_id || null,
        sentTime: remoteMessage.sentTime || null,
        from: remoteMessage.from || null,
        collapseKey: remoteMessage.collapseKey || null,
        notification: {
            title: notification.title || data.title || '',
            body: notification.body || data.body || '',
        },
        data,
        raw: remoteMessage,
    };
}

export function addForegroundMessageListener(listener) {
    return messaging().onMessage(async (remoteMessage) => {
        const normalizedMessage = normalizeRemoteMessage(remoteMessage);
        await listener(normalizedMessage);
    });
}

export function addTokenRefreshListener(listener) {
    return messaging().onTokenRefresh(async (token) => {
        await listener(token);
    });
}

export function addNotificationOpenedListener(listener) {
    return messaging().onNotificationOpenedApp(async (remoteMessage) => {
        const normalizedMessage = normalizeRemoteMessage(remoteMessage);
        await listener(normalizedMessage);
    });
}

export async function getInitialOpenNotification() {
    const remoteMessage = await messaging().getInitialNotification();
    return normalizeRemoteMessage(remoteMessage);
}

export async function savePendingOpenNotification(message) {
    await persistPushNotificationState({ pendingOpenMessage: message || null });
}

export async function consumePendingOpenNotification() {
    const storedState = await loadStoredPushNotificationState();

    if (storedState.pendingOpenMessage) {
        await persistPushNotificationState({ pendingOpenMessage: null });
    }

    return storedState.pendingOpenMessage;
}

export function setPushBackgroundMessageHandler(handler) {
    messaging().setBackgroundMessageHandler(async (remoteMessage) => {
        const normalizedMessage = normalizeRemoteMessage(remoteMessage);
        if (handler) {
            await handler(normalizedMessage);
        }
    });
}





